import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

import SegStatusBar from './SegStatusBar';
import SegHistoryDropdown from './SegHistoryDropdown';
import DownloadScanBtn from './DownloadScanBtn';
import OverwriteScanBtn from './OverwriteScanBtn';
import RejectScanBtn from './RejectScanBtn';
import RunSegBtn from './RunSegBtn';
import VerifySegBtn from './VerifySegBtn';
import ConfirmPatientInfoBtn from './ConfirmPatientInfoBtn';

import { SCAN_STATUS, SEG_STATUS } from '../../../settings/segmentations';

import goBack from '../../../images/go-back.svg';
import logo from '../../../images/logo-teal.svg';

import { verifyPatientDBInfo } from '../../../utils/dicom/dicom';

import useSelectorToJS from '../../../hooks/useSelectorToJS';
import { getSelectedVol } from '../../../modules/viewer/selectors';

export default function ViewerHeader({ seg, scan }) {
  const history = useHistory();

  const { vol } = useSelectorToJS(({ viewer, volume3Ds }) => ({
    vol: getSelectedVol(viewer, volume3Ds),
  }));

  const hasPendingSeeds = seg.seedPoints.filter(seed => seed.pending).length > 0;
  const patientDBInfo = verifyPatientDBInfo(vol, scan);

  const showRunSegBtn =
    hasPendingSeeds ||
    seg.status === SEG_STATUS.CREATED ||
    seg.status === SEG_STATUS.RUNNING ||
    seg.status === SEG_STATUS.ERROR;

  const showConfirmPatientInfoBtn =
    !showRunSegBtn &&
    !patientDBInfo.isScanAnonymized &&
    !patientDBInfo.isVolAnonymized &&
    patientDBInfo.personalInfo.volName !== patientDBInfo.personalInfo.scanName &&
    patientDBInfo.personalInfo.volMRN === patientDBInfo.personalInfo.scanMRN &&
    patientDBInfo.personalInfo.volDOB === patientDBInfo.personalInfo.scanDOB;

  const showVerifySegBtn = !showRunSegBtn && !showConfirmPatientInfoBtn;

  const handleBackClick = () => {
    history.push('/scans');
  };

  return (
    <Container>
      <FixedWidth>
        <BackBtn onClick={handleBackClick}>
          <img src={goBack} alt='Go Back' />
        </BackBtn>
        <Logo src={logo} alt='VisionAir Solutions' />
      </FixedWidth>

      <DynamicWidth>
        <SegStatusBar seg={seg} scan={scan} />
        <SegHistoryDropdown scan={scan} />
      </DynamicWidth>

      <ButtonContainer>
        <DownloadScanBtn seg={seg} scan={scan} />

        {scan.status !== SCAN_STATUS.VERIFIED && scan.status !== SCAN_STATUS.REJECTED && (
          <OverwriteScanBtn scan={scan} />
        )}

        <RejectScanBtn scanStatus={scan.status} />

        {showRunSegBtn && <RunSegBtn scanID={scan.id} seg={seg} scanStatus={scan.status} />}

        {showConfirmPatientInfoBtn && (
          <ConfirmPatientInfoBtn
            volID={vol.id}
            volName={patientDBInfo.personalInfo.volName}
            scanName={patientDBInfo.personalInfo.scanName}
          />
        )}

        {showVerifySegBtn && <VerifySegBtn scanStatus={scan.status} />}
      </ButtonContainer>
    </Container>
  );
}

ViewerHeader.propTypes = {
  seg: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    seedPoints: PropTypes.array,
  }).isRequired,
  scan: PropTypes.shape({
    id: PropTypes.string,
    scanSegStatus: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

const Container = styled.div`
  height: 70px;
  min-width: 1280px;
  padding: 12px 25px;

  background-color: #141414;

  display: flex;
  justify-content: flex-start;
`;

const FixedWidth = styled.div`
  display: flex;
  align-items: center;
`;

const DynamicWidth = styled.div`
  display: flex;
  flex: 1 0 auto;
  justify-content: flex-start;
  padding: 0 25px;
`;

const BackBtn = styled.button`
  background: transparent;
  border: none;
  :focus {
    outline: none;
  }
`;

const Logo = styled.img`
  margin-left: 15px;
  width: 45px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 25px;
`;
